import { OnboardingStatus, OnboardingStepType, OrganizationDetailFragment } from '@alice-financial/alice-sdk'
import {
  Box,
  Button,
  Card,
  CardContent,
  ChipProps,
  Grid,
  Link,
  Tooltip,
  Typography,
  TypographyProps,
} from '@alice-financial/pretext-ui'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import EditIcon from '@mui/icons-material/Edit'
import * as React from 'react'
import { useNavigate } from 'react-router'
import { HealthcheckAssignmentDialog } from '../components/HealthcheckAssignmentDialog'
import { useOrg } from '../organization/useOrg'
import { HealthcheckAssignee } from '../types'
import { useOrgId } from '../useOrgId'
import {
  buildOnboardingPath,
  getOnboardingStepAssignee,
  getStep,
  useStepNavigationState,
} from '../utils/onboardingWorkflowUtils'
import { getStepStatus } from './getStepStatus'
import { getStepConfig } from './stepConfiguration'
import { StickyInstructions } from './StickyInstructions'

type StepHeaderProps = {
  org: OrganizationDetailFragment | null | undefined
  stepType: OnboardingStepType
}
const StepHeader = ({ org, stepType }: StepHeaderProps) => {
  const [assignmentDialogOpen, setAssignmentDialogOpen] = React.useState(false)

  const stepConfig = getStepConfig(stepType)
  const step = getStep(org?.onboardingWorkflow, stepType)
  const assignee = getOnboardingStepAssignee(org?.onboardingWorkflow, stepType)

  const handleOpenAssignmentDialog = () => {
    setAssignmentDialogOpen(true)
  }

  const handleCloseAssignmentDialog = () => {
    setAssignmentDialogOpen(false)
  }

  const getAssigneeDisplayName = (assignee?: HealthcheckAssignee | null): string => {
    if (!assignee) return 'Not assigned'
    if (assignee.firstName && assignee.lastName) {
      return `${assignee.firstName} ${assignee.lastName}`
    }
    return assignee.name || assignee.email || 'Unknown user'
  }

  return (
    <CardContent
      sx={(theme) => ({
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        p: 1.5,
        '&:last-child': { pb: 1.5 },
      })}
    >
      <Grid container spacing={2} width="auto" py={1}>
        {/* Step Header */}
        {/* Left Column - Title and Description */}
        <Grid flex={1}>
          <Typography variant="h1" color="primary.contrastText" sx={{ mb: 0 }}>
            {stepConfig.stepTitle}
          </Typography>
        </Grid>
        {/* Status Section */}
        <Grid>
          <StatusHeading>Status</StatusHeading>
          <Box sx={{ mt: 0.5 }}>
            <StatusChip step={step} />
          </Box>
        </Grid>

        {/* Assignee Section - only show if not completed */}
        {
          <Grid borderLeft={1} borderColor="grey.400" pl={2}>
            <StatusHeading>Assignee</StatusHeading>
            <Tooltip title="The assigned person will receive notifications and be responsible for completing this step. Click to reassign this step.">
              <StatusValue
                component={Link}
                onClick={handleOpenAssignmentDialog}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  '&:hover': {
                    color: 'white !important',
                  },
                }}
              >
                {getAssigneeDisplayName(assignee)}{' '}
                {step?.status !== OnboardingStatus.Completed && (
                  <EditIcon color="inherit" fontSize="inherit" />
                )}
              </StatusValue>
            </Tooltip>
          </Grid>
        }
      </Grid>
      {/* Assignment Dialog */}
      <HealthcheckAssignmentDialog
        open={assignmentDialogOpen}
        onClose={handleCloseAssignmentDialog}
        stepType={stepType}
        healthcheckTitle={stepConfig.stepTitle}
        currentAssignee={
          assignee
            ? {
                id: assignee.id,
                name: assignee.name,
                email: assignee.email,
              }
            : null
        }
      />
    </CardContent>
  )
}

const StepNav = ({
  stepType,
  org,
}: {
  stepType: OnboardingStepType
  org: OrganizationDetailFragment | null | undefined
}) => {
  const orgId = useOrgId()
  const { nextStep, previousStep } = useStepNavigationState(org)
  const stepStatus = getStepStatus(stepType, org)
  const navigate = useNavigate()

  const handlePreviousStep = () => {
    if (previousStep) {
      navigate(buildOnboardingPath(orgId, previousStep.stepType))
    }
  }

  const handleNextStep = () => {
    if (nextStep) {
      navigate(buildOnboardingPath(orgId, nextStep.stepType))
    }
  }

  return (
    <Grid container spacing={2} justifyContent="space-between">
      <Grid size="auto">
        {previousStep && (
          <Button variant="text" size="small" startIcon={<ChevronLeftIcon />} onClick={handlePreviousStep}>
            {previousStep.stepTitle}
          </Button>
        )}
      </Grid>
      <Grid size="auto">
        {nextStep && (
          <Button
            variant={stepStatus.completed ? 'contained' : 'outlined'}
            size="small"
            endIcon={<ChevronRightIcon />}
            onClick={handleNextStep}
          >
            Next: {nextStep.stepTitle}
          </Button>
        )}
      </Grid>
    </Grid>
  )
}

const StatusHeading = (props: TypographyProps) => (
  <Typography
    color="primary.contrastText"
    variant="subtitle2"
    fontSize="0.7rem"
    textTransform="uppercase"
    {...props}
  />
)
const StatusValue = (props: TypographyProps) => (
  <Typography color="primary.contrastText" fontWeight="bold" fontSize="1rem" {...props} />
)

interface OnboardingStepWrapperProps {
  stepType: OnboardingStepType
  children: React.ReactNode
}

interface StatusChipProps {
  step: { status: OnboardingStatus } | null | undefined
}

/**
 * Pure component for displaying step status as a chip
 */
const StatusChip: React.FC<StatusChipProps> = ({ step }) => {
  if (!step) return null

  const statusConfig: Record<OnboardingStatus, { label: string; color: ChipProps['color'] }> = {
    [OnboardingStatus.Completed]: { label: 'Completed', color: 'primary' as const },
    [OnboardingStatus.InProgress]: { label: 'In Progress', color: 'primary' as const },
    [OnboardingStatus.NotStarted]: { label: 'Not Started', color: 'default' as const },
    [OnboardingStatus.Blocked]: { label: 'Blocked', color: 'error' as const },
  }

  const config = statusConfig[step.status]
  return config ? <StatusValue textTransform="uppercase">{config.label}</StatusValue> : null
}

/**
 * Wrapper component for individual onboarding steps
 * Provides assignment section, step content, and navigation controls
 */
export const OnboardingStepWrapper = ({ stepType, children }: OnboardingStepWrapperProps) => {
  const org = useOrg()

  const stepConfig = getStepConfig(stepType)

  if (!org) {
    return <div>Loading...</div>
  }

  return (
    <>
      {/* Main Step Content */}
      {/* Step Content Column */}
      <Grid container spacing={3} flexDirection="row-reverse">
        <Grid size={{ xs: 12, lg: 4 }}>
          {/* Instructions Column */}
          <StickyInstructions stepConfig={stepConfig} stepType={stepType} />
        </Grid>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <StepHeader org={org} stepType={stepType} />
            <CardContent>{children}</CardContent>
          </Card>
          {/* Navigation Controls */}
          <Box pt={2} mt={4}>
            <StepNav stepType={stepType} org={org} />
          </Box>
        </Grid>
      </Grid>
    </>
  )
}
